"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronRight, List } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TocHeading } from "@/lib/blog-toc";

interface TableOfContentsProps {
  headings: TocHeading[];
  label: string;
}

export function TableOfContents({ headings, label }: TableOfContentsProps) {
  // Only show top-level sections (H2) to keep the sidebar compact.
  // H2s remain the natural anchors; H3s are reachable via in-section scroll.
  const topHeadings = useMemo(
    () => headings.filter((h) => h.level === 2),
    [headings]
  );

  const [activeId, setActiveId] = useState<string | null>(
    topHeadings[0]?.id ?? null
  );
  const [open, setOpen] = useState(false);

  const ids = useMemo(() => topHeadings.map((h) => h.id), [topHeadings]);

  useEffect(() => {
    if (ids.length === 0) return;
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    const visible = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        });
        // pick the first heading (document order) currently in view
        const firstVisible = ids.find((id) => visible.has(id));
        if (firstVisible) {
          setActiveId(firstVisible);
        }
      },
      {
        rootMargin: "-80px 0px -60% 0px",
        threshold: 0,
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [ids]);

  if (topHeadings.length < 2) return null;

  return (
    <>
      {/* Desktop: sidebar (sticky is applied by the parent grid cell) */}
      <aside className="hidden lg:block pr-2">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <nav>
          <ul className="space-y-1 border-l border-border">
            {topHeadings.map((h) => {
              const isActive = h.id === activeId;
              return (
                <li key={h.id}>
                  <a
                    href={`#${h.id}`}
                    className={cn(
                      "block border-l-2 py-1.5 pl-4 text-sm leading-snug transition-colors -ml-px",
                      isActive
                        ? "border-primary text-primary font-medium"
                        : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/40"
                    )}
                  >
                    {h.text}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>

      {/* Mobile/tablet: collapsible card */}
      <div className="lg:hidden mb-8 rounded-xl border bg-card">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center justify-between px-4 py-3 text-left"
          aria-expanded={open}
        >
          <span className="flex items-center gap-2 text-sm font-semibold">
            <List className="h-4 w-4 text-primary" />
            {label}
          </span>
          <ChevronRight
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              open && "rotate-90"
            )}
          />
        </button>
        {open && (
          <nav className="border-t px-4 py-3">
            <ul className="space-y-1">
              {topHeadings.map((h) => (
                <li key={h.id}>
                  <a
                    href={`#${h.id}`}
                    onClick={() => setOpen(false)}
                    className={cn(
                      "block rounded-md py-1.5 pl-2 text-sm transition-colors",
                      h.id === activeId
                        ? "text-primary font-medium"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {h.text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </>
  );
}
